/**
 * Stored Contract Archive Picker Helpers.
 *
 * Formats epoch dates, contract strike summaries, and queries the
 * hierarchical stored-contracts tree (underlying -> expiry -> contracts).
 */

export interface ArchiveContractItem {
  symbol: string;
  strike: number | string;
  side: string;
  rows: number;
  first_time?: number;
  last_time?: number;
  source?: string[];
  [key: string]: any;
}

/**
 * Formats a UNIX epoch timestamp (in seconds) into YYYY-MM-DD string.
 */
export function formatArchiveDate(epoch?: number | null): string {
  if (!epoch) return "?";
  const d = new Date(epoch * 1000);
  return d.toISOString().slice(0, 10);
}

/**
 * Builds the descriptive display label for an archived contract dropdown item.
 */
export function formatArchiveStrikeLabel(item: ArchiveContractItem): string {
  const span = `${formatArchiveDate(item.first_time)}→${formatArchiveDate(item.last_time)}`;
  const src = (item.source || []).join("+");
  return `${item.strike} ${item.side} — ${item.rows} bars, ${span} [${src}]`;
}

/**
 * Extracts sorted expiry date strings for a given underlying symbol from the archive tree.
 */
export function extractArchiveExpiries(
  tree:
    | Record<string, Record<string, ArchiveContractItem[]>>
    | null
    | undefined,
  underlying: string,
): string[] {
  if (!tree || !underlying) return [];
  const expiriesObj = tree[underlying] || {};
  return Object.keys(expiriesObj).sort();
}

/**
 * Extracts contract items for a given underlying and expiry date.
 */
export function extractArchiveStrikes(
  tree:
    | Record<string, Record<string, ArchiveContractItem[]>>
    | null
    | undefined,
  underlying: string,
  expiry: string,
): ArchiveContractItem[] {
  if (!tree || !underlying || !expiry) return [];
  return (tree[underlying] || {})[expiry] || [];
}

/**
 * Finds a specific contract item matching a trading symbol.
 */
export function findArchiveContract(
  tree:
    | Record<string, Record<string, ArchiveContractItem[]>>
    | null
    | undefined,
  underlying: string,
  expiry: string,
  symbol: string,
): ArchiveContractItem | null {
  const items = extractArchiveStrikes(tree, underlying, expiry);
  return items.find((i) => i.symbol === symbol) || null;
}
